import { EditorState } from '@tiptap/pm/state'

import { COMMENT_ANCHOR_MARK_NAME } from '../../../../../../backend/src/tiptap/editor/extensions/marks/comment-anchor'

export type CommentAnchorRange = {
  anchorId: string
  from: number
  to: number
}

// A comment mark can be split into several fragments by edits inside its range.
// All fragments share one anchorId; we collapse them to the widest span
// (earliest start, latest end) so the comment reads as a single range.
export function collectCommentAnchors(state: EditorState): Map<string, CommentAnchorRange> {
  const ranges = new Map<string, CommentAnchorRange>()

  state.doc.descendants((node, pos) => {
    if (!node.isText) {
      return true
    }
    const mark = node.marks.find((candidate) => candidate.type.name === COMMENT_ANCHOR_MARK_NAME) ?? null
    if (mark === null) {
      return false
    }
    const anchorId = mark.attrs['anchorId'] as string | null
    if (anchorId === null) {
      return false
    }
    const from = pos
    const to = pos + node.nodeSize
    const existing = ranges.get(anchorId) ?? null
    if (existing === null) {
      ranges.set(anchorId, { anchorId, from, to })
    } else {
      existing.from = Math.min(existing.from, from)
      existing.to = Math.max(existing.to, to)
    }
    return false
  })

  return ranges
}

export function collectCommentAnchorStartPositions(state: EditorState): number[] {
  return [...collectCommentAnchors(state).values()].map((range) => range.from).sort((left, right) => left - right)
}

function commentAnchorText(state: EditorState, range: CommentAnchorRange): string {
  return state.doc.textBetween(range.from, range.to, ' ')
}

export type CommentAnchorSummary = {
  anchorId: string
  quotedText: string
  position: number
}

// `includeText` is skipped when the sidebar is closed: only the anchor ids and
// positions feed the toolbar and navigation there, so the per-anchor textBetween
// read (the only quoted-text consumer is the sidebar card) is deferred.
export function collectCommentAnchorSummaries(state: EditorState, includeText = true): CommentAnchorSummary[] {
  return [...collectCommentAnchors(state).values()]
    .map((range) => ({ anchorId: range.anchorId, quotedText: includeText ? commentAnchorText(state, range) : '', position: range.from }))
    .sort((left, right) => left.position - right.position)
}

export function commentAnchorSummariesEqual(left: CommentAnchorSummary[], right: CommentAnchorSummary[]): boolean {
  if (left.length !== right.length) {
    return false
  }
  return left.every((entry, index) => {
    const other = right[index]
    return entry.anchorId === other.anchorId && entry.quotedText === other.quotedText && entry.position === other.position
  })
}

export function findCommentAnchorIdAtPosition(state: EditorState, position: number): string | null {
  const resolved = state.doc.resolve(position)
  const marks = [...resolved.marks(), ...(resolved.nodeBefore?.marks ?? []), ...(resolved.nodeAfter?.marks ?? [])]
  const mark = marks.find((candidate) => candidate.type.name === COMMENT_ANCHOR_MARK_NAME) ?? null
  return mark !== null ? ((mark.attrs['anchorId'] as string | null) ?? null) : null
}
